/**
 * The install runtime: holds Chrome's `beforeinstallprompt`, hears `appinstalled`, knows
 * the platform, and keeps the invitation's memory (install-state.ts) and its analytics
 * (Spec panel 3) in one place, so the surfaces (the Today card, the ⋯ menu line, the
 * Stats row, the desktop button) stay thin. React reads it through `useInstall()`.
 * Nothing here throws into the UI.
 */
import { type DateKey, daysBetween, localDateKey } from '@arrow/game-engine';
import { useSyncExternalStore } from 'react';

import { claimCarry, createCarry, fetchCarry } from '../api/client.ts';
import { track } from '../platform/analytics.ts';
import { platformStore } from '../platform/config.ts';
import { getDeviceId } from '../platform/identity.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { adoptSnapshot, buildSnapshot, CARRY_PARAM, carryCodeIn, combineSnapshot, isLocalStateEmpty, mergeSnapshot, withCarry } from './carry.ts';
import {
	afterGuideDone,
	afterNotNow,
	daysSinceInstall,
	type InstallState,
	loadInstallState,
	migrateInstallState,
	noteInstalledOn,
	noteShown,
	saveInstallState,
	type Surface,
} from './install-state.ts';
import { codeUsed, MOVE_NOTE_KEY, MOVED_PARAM, noteCodeUsed, readMoveFragment } from './move.ts';
import { installRoute, type InstallRoute, type PlatformInfo, readPlatform } from './platform.ts';

/** Chrome's held install prompt. */
interface InstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallSnapshot {
	platform: PlatformInfo;
	/** Chrome / Edge handed us a prompt to show. */
	held: boolean;
	/** `appinstalled` fired during this page load (the confirmation card). */
	installedNow: boolean;
	/**
	 * Sparrow is installed, detected, never remembered: running from the home screen
	 * (`platform.standalone`), `appinstalled` this page load, or (in a Chromium tab)
	 * `getInstalledRelatedApps()` finding it. A `beforeinstallprompt` means it isn't.
	 * Where nothing can tell (an iPhone Safari tab, Firefox): false.
	 */
	installed: boolean;
	/** The route our button takes here, or null (nothing to offer). */
	route: InstallRoute | null;
	/** Bumps on every change to the stored state, so readers re-read it. */
	rev: number;
}

let store: KeyValueStore = platformStore;
let held: InstallPromptEvent | null = null;
let platform: PlatformInfo | null = null;
let installedNow = false;
/** getInstalledRelatedApps' last answer this page load (null: not asked, or can't). */
let related: boolean | null = null;
let rev = 0;
let snapshot: InstallSnapshot | null = null;
const listeners = new Set<() => void>();

const today = () => localDateKey(new Date());

function emit() {
	rev++;
	snapshot = null;
	for (const fn of listeners) fn();
}

function current(): InstallSnapshot {
	platform ??= readPlatform();
	snapshot ??= {
		platform,
		held: held !== null,
		installedNow,
		installed: platform.standalone || installedNow || related === true,
		route: installRoute(platform, held !== null),
		rev,
	};
	return snapshot;
}

let started = false;

/**
 * Start listening (once, as early as possible: Chrome fires `beforeinstallprompt` soon
 * after load, and it must be caught then).
 */
export function initInstall(): void {
	if (started || typeof window === 'undefined') return;
	started = true;
	// Older versions stored an "installed" mark that hid the install line for good after an uninstall: drop it.
	try {
		migrateInstallState(store);
	} catch {
		// Storage is best-effort.
	}
	window.addEventListener('beforeinstallprompt', (e) => {
		// Stops Chrome's own mini-infobar; we show the prompt from our button instead.
		e.preventDefault();
		held = e as InstallPromptEvent;
		// Chrome only offers this when Sparrow isn't installed.
		related = false;
		installedNow = false;
		emit();
	});
	window.addEventListener('appinstalled', () => {
		held = null;
		installedNow = true;
		try {
			const state = loadInstallState(store);
			if (!state.installedOn) saveInstallState(store, noteInstalledOn(state, today()));
		} catch {
			// Analytics memory only.
		}
		track('installed', { platform: current().platform.analytics, via: 'appinstalled' });
		emit();
	});
	void checkRelatedApps();
	// Uninstalled (or installed) while this tab sat in the background: ask again on return.
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') void checkRelatedApps();
	});
}

interface RelatedAppsNavigator {
	getInstalledRelatedApps?: () => Promise<unknown[]>;
}

/**
 * In a browser tab, ask `navigator.getInstalledRelatedApps()` whether Sparrow is installed
 * (the manifest lists Sparrow itself as a `webapp` related application; Chromium answers,
 * Safari and Firefox don't have it). The answer is kept in memory for this page load only.
 * Never throws.
 */
export async function checkRelatedApps(nav: RelatedAppsNavigator | undefined = typeof navigator === 'undefined' ? undefined : (navigator as RelatedAppsNavigator)): Promise<void> {
	try {
		if (current().platform.standalone || typeof nav?.getInstalledRelatedApps !== 'function') return;
		const apps = await nav.getInstalledRelatedApps();
		const found = Array.isArray(apps) && apps.length > 0;
		// A prompt held right now says otherwise, and it's the fresher word.
		const next = found && held === null;
		if (next === related) return;
		related = next;
		emit();
	} catch {
		// Unsupported or refused: we can't tell, so we assume it isn't installed.
	}
}

export function useInstall(): InstallSnapshot {
	return useSyncExternalStore(
		(fn) => {
			listeners.add(fn);
			return () => listeners.delete(fn);
		},
		current,
		current,
	);
}

export function installState(): InstallState {
	return loadInstallState(store);
}

function update(fn: (s: InstallState) => InstallState): InstallState {
	const next = fn(loadInstallState(store));
	saveInstallState(store, next);
	emit();
	return next;
}

// --- Analytics + memory, per surface ------------------------------------------------------

/** A surface came on screen: `install_prompt_shown`, once per surface per day. */
export function noteSurfaceShown(surface: Surface, day: DateKey = today()): void {
	const res = noteShown(loadInstallState(store), surface, day);
	if (!res.first) return;
	saveInstallState(store, res.state);
	track('install_prompt_shown', { surface, platform: current().platform.analytics, timesShown: res.times });
}

export function noteAccepted(surface: Surface, step: 'cta' | 'native' | 'guide_done' | 'way_out'): void {
	track('install_prompt_accepted', { surface, platform: current().platform.analytics, step });
}

/** Turned down: rests the card 7 → 30 days → for good. */
export function noteDismissed(surface: Surface, via: 'not_now' | 'native_cancel' | 'guide_closed', day: DateKey = today()): void {
	const next = update((s) => afterNotNow(s, day));
	track('install_prompt_dismissed', { surface, platform: current().platform.analytics, via, count: next.dismissals });
}

/** "It didn't work" in the iPhone guide: tracked; no rest (they're still trying). */
export function noteDidntWork(surface: Surface): void {
	track('install_prompt_dismissed', { surface, platform: current().platform.analytics, via: 'didnt_work', count: loadInstallState(store).dismissals });
}

/** "Done" in the iPhone guide: rests the card 30 days. */
export function noteGuideDone(surface: Surface, day: DateKey = today()): void {
	update((s) => afterGuideDone(s, day));
	noteAccepted(surface, 'guide_done');
}

/** Today's card decision was made: remember it (install-state `decideCard`). */
export function saveCardDecision(state: InstallState): void {
	const now = loadInstallState(store);
	if (now.cardDay?.day === state.cardDay?.day) return;
	saveInstallState(store, { ...now, cardDay: state.cardDay });
}

/**
 * Chrome's own sheet, from our button. Resolves with what the person chose; 'unavailable'
 * if there's no held prompt (it's single-use, and Chrome may offer a new one later).
 */
export async function promptNative(surface: Surface): Promise<'accepted' | 'dismissed' | 'unavailable'> {
	const event = held;
	if (!event) return 'unavailable';
	held = null;
	emit();
	try {
		await event.prompt();
		const { outcome } = await event.userChoice;
		if (outcome === 'accepted') noteAccepted(surface, 'native');
		else noteDismissed(surface, 'native_cancel');
		return outcome;
	} catch {
		return 'unavailable';
	}
}

// --- Launch: standalone bookkeeping -----------------------------------------------------------

/**
 * On every launch from the home screen: `opened_standalone`, and the first time on this
 * storage, `installed { via: 'first_standalone_open' }` (iOS can't tell us otherwise).
 */
export function noteLaunch(day: DateKey = today()): void {
	try {
		const p = current().platform;
		if (!p.standalone) return;
		let state = loadInstallState(store);
		if (!state.installedOn) {
			state = noteInstalledOn(state, day);
			saveInstallState(store, state);
			track('installed', { platform: p.analytics, via: 'first_standalone_open' });
		}
		track('opened_standalone', { platform: p.analytics, daysSinceInstall: daysSinceInstall(state, day, daysBetween) });
	} catch {
		// Never breaks the launch.
	}
}

// --- Carry-over --------------------------------------------------------------------------------

let carried: { code: string; until: number } | null = null;
let renewal: ReturnType<typeof setTimeout> | null = null;
let renewals = 0;
/** A code is renewed this long before it expires, and at most this many times a page load (about two hours). */
const RENEW_BEFORE_MS = 5 * 60 * 1000;
const MAX_RENEWALS = 4;

/**
 * Before the iPhone guide or a way-out link opens: a one-time code for this device's
 * state, put in the address (`?carry=`) and the manifest link. Reuses a code with 5+
 * minutes left. Resolves with the code, or null (offline: the guide still opens).
 *
 * While the page stays open and in view, the code is renewed shortly before it expires
 * (for about two hours), so adding Sparrow to the home screen a while after the guide
 * still brings the device's id, streak and nests (a fresh snapshot each time).
 */
export async function prepareCarry(day: DateKey = today()): Promise<string | null> {
	if (carried && carried.until - Date.now() > RENEW_BEFORE_MS) return carried.code;
	try {
		const snapshot = buildSnapshot(store, day, new Date());
		const res = await createCarry(getDeviceId(store), snapshot);
		if (!res) return null;
		const until = Date.parse(res.expiresAt);
		carried = { code: res.code, until: Number.isFinite(until) ? until : Date.now() + 30 * 60 * 1000 };
		putCarryInPage(res.code);
		keepCarryFresh();
		return res.code;
	} catch {
		return null;
	}
}

function keepCarryFresh() {
	if (renewal || !carried || renewals >= MAX_RENEWALS || typeof window === 'undefined') return;
	renewal = setTimeout(() => {
		renewal = null;
		// Only while someone's looking: a tab left in the background doesn't keep making codes.
		if (document.visibilityState !== 'visible') {
			document.addEventListener('visibilitychange', keepCarryFresh, { once: true });
			return;
		}
		renewals++;
		void prepareCarry();
	}, Math.max(10_000, carried.until - Date.now() - RENEW_BEFORE_MS));
}

/** The address bar (whichever URL iOS saves) and the manifest link carry the code. */
function putCarryInPage(code: string) {
	try {
		window.history.replaceState(window.history.state, '', withCarry(window.location.href, code));
		const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
		if (link) link.href = `/manifest.webmanifest?${CARRY_PARAM}=${encodeURIComponent(code)}`;
	} catch {
		// The guide still works; the streak just doesn't come along.
	}
}

/** The current address with this page's carry code, for the way-out link and Copy link. */
export function carriedUrl(code: string | null): string {
	return new URL(withCarry(window.location.href, code), window.location.origin).toString();
}

/**
 * On launch, before anything reads storage:
 * - `?carry=CODE` (the home-screen app's first launch, from Safari): with nothing played
 *   here yet, fetch the snapshot and merge it (same device id);
 * - `#carry=CODE` (the move from the old address, pwa/move.ts), or `?carry=CODE&moved=1`
 *   (a link out of the old home-screen app): fetch it and merge it into whatever is here
 *   (`combineSnapshot`), once per code, and leave Today a note: it came across, or (it
 *   didn't, or `#carry-failed`) try again.
 * The params, the fragment and the manifest's `source=homescreen` are stripped from the
 * address either way. Waits at most `timeoutMs`.
 */
export async function consumeCarryOnLaunch(timeoutMs = 4000): Promise<void> {
	if (typeof window === 'undefined') return;
	try {
		const url = new URL(window.location.href);
		const handOver = readMoveFragment(url.hash);
		const queryCode = carryCodeIn(url.href);
		const moved = handOver !== null || url.searchParams.has(MOVED_PARAM);
		if (!handOver && !queryCode && !url.searchParams.has('source') && !moved) return;
		url.searchParams.delete(CARRY_PARAM);
		url.searchParams.delete(MOVED_PARAM);
		if (url.searchParams.get('source') === 'homescreen') url.searchParams.delete('source');
		if (handOver) url.hash = '';
		const q = url.searchParams.toString();
		window.history.replaceState(window.history.state, '', `${url.pathname}${q ? `?${q}` : ''}${url.hash}`);
		if (handOver && 'failed' in handOver) {
			store.set(MOVE_NOTE_KEY, 'failed');
			return;
		}
		const code = handOver?.code ?? queryCode;
		if (!code) return;
		if (codeUsed(store, code)) return;
		// The home-screen app opened before with an id of its own (it was added from the share
		// sheet, or opened once already): claim the code, so the api folds that id into the
		// carried one, and adopt it. In a browser tab, a played device keeps its own.
		const claim = !moved && !isLocalStateEmpty(store);
		if (claim && !current().platform.standalone) return;
		const ctrl = new AbortController();
		const timer = setTimeout(() => ctrl.abort(), timeoutMs);
		const data = await (claim ? claimCarry(code, getDeviceId(store), ctrl.signal) : fetchCarry(code, ctrl.signal)).finally(() => clearTimeout(timer));
		const res = data ? (claim ? adoptSnapshot(store, data) : moved ? combineSnapshot(store, data) : mergeSnapshot(store, data)) : null;
		if (res?.merged) noteCodeUsed(store, code);
		if (moved) store.set(MOVE_NOTE_KEY, res?.merged ? 'ok' : 'failed');
	} catch {
		// Start fresh rather than not at all.
	}
}

/**
 * A carry code typed into the home-screen app by hand (the Nest tab's "Bring your nests
 * here"): claim it, so the api folds this app's own id into the carried one, then adopt
 * it. Resolves true when it worked; the caller reloads, since the device id changed.
 */
export async function claimTypedCode(raw: string, timeoutMs = 8000): Promise<boolean> {
	const code = raw.replace(/[\s-]+/g, '').toUpperCase();
	if (!/^[A-Z0-9]{4,8}$/.test(code)) return false;
	try {
		const ctrl = new AbortController();
		const timer = setTimeout(() => ctrl.abort(), timeoutMs);
		const data = await claimCarry(code, getDeviceId(store), ctrl.signal).finally(() => clearTimeout(timer));
		const res = data ? adoptSnapshot(store, data) : null;
		if (!res?.merged) return false;
		noteCodeUsed(store, code);
		return true;
	} catch {
		return false;
	}
}

/** Tests: a fresh runtime over `kv`, as if on `p`. */
export function resetInstallForTests(kv: KeyValueStore = platformStore, p: PlatformInfo | null = null): void {
	store = kv;
	held = null;
	platform = p;
	installedNow = false;
	related = null;
	carried = null;
	if (renewal) clearTimeout(renewal);
	renewal = null;
	renewals = 0;
	emit();
}

/** Tests: pretend Chrome handed us a prompt. */
export function holdPromptForTests(event: { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> } | null): void {
	held = event as InstallPromptEvent | null;
	emit();
}
